import { useMemo, useState } from 'react';

import { Box, Flex, Menu, Text, useMantineTheme } from 'ui';
import { IconDots } from 'ui/Icons';

import { Printty } from '../../Printty/printty';
import { useContextMenu } from './TableBody/row/useContextMenu';

// Safe hook that doesn't throw when context is missing
const useSafeRightClickContextMenu = () => {
  try {
    return useContextMenu();
  } catch {
    return {
      info: { opened: false, x: 0, y: 0 },
      closeContextMenu: () => {},
      setInfo: () => {},
    };
  }
};

const TableRowActionOption = ({
  label,
  icon,
  onClick,
  visible = true,
  destructive = false,
}: {
  onClick: () => void;
  icon?: (props: { size: number; color: string }) => React.JSX.Element;
  label: string;
  visible?: boolean;
  destructive?: boolean;
}) => {
  const theme = useMantineTheme();
  const Icon = icon;

  const labelColor = destructive ? 'cerise.7' : 'gray';
  const iconColor = destructive ? theme.colors.cerise[6] : theme.colors.gray[4];

  if (!visible) return null;

  return (
    <Menu.Item onClick={onClick} h="40">
      <Flex align="center" gap="xs" h={40}>
        {Icon && <Icon size={12} color={iconColor} />}

        <Text c={labelColor} size="sm">
          {label}
        </Text>
      </Flex>
    </Menu.Item>
  );
};

interface TableRowActionProps {
  children: React.ReactNode | React.ReactNode[];
  onOpen?: () => void;
  onMouseEnter?: () => void;
}

const TableRowAction = ({ children, onOpen, onMouseEnter }: TableRowActionProps) => {
  const [opened, setOpened] = useState(false);
  const [isRegularMenuExiting, setIsRegularMenuExiting] = useState(false);
  const { noPrintRef } = Printty.useRemovePrintElement();

  const { info, closeContextMenu } = useSafeRightClickContextMenu();

  // Condition for this menu to be open due to right-click on *this* row
  const isContextOpenForThisRow = useMemo(() => info.opened, [info.opened]);

  // Overall open state for this menu instance
  const isMenuOpen = useMemo(
    () => opened || isContextOpenForThisRow,
    [opened, isContextOpenForThisRow]
  );

  const isRegularMenuExitTransitioning = useMemo(
    () => !isContextOpenForThisRow && isRegularMenuExiting,
    [isContextOpenForThisRow, isRegularMenuExiting]
  );

  return (
    <Menu
      opened={isMenuOpen}
      onOpen={() => {
        // optional callback to be called when the menu is opened
        onOpen?.();

        // reset the exiting state
        setIsRegularMenuExiting(false);
      }}
      onClose={() => {
        setIsRegularMenuExiting(true);
        setOpened(false);
        closeContextMenu();
      }}
      position="bottom-end"
    >
      <Menu.Target>
        <Box
          onMouseEnter={onMouseEnter}
          component="button"
          bg="white"
          style={(theme) => ({
            borderRadius: theme.radius.xl,
            border: '0px solid transparent',
          })}
          onClick={() => setOpened((opened) => !opened)}
          p={4}
        >
          <Box
            // noPrintRef is used to prevent the icon from being printed in pdf/print
            ref={noPrintRef}
            style={() =>
              opened
                ? {
                    transform: 'rotate(90deg)',
                    transition: '100ms ease all',
                  }
                : { transition: '100ms ease all' }
            }
          >
            <IconDots color="#234DD4" />
          </Box>
        </Box>
      </Menu.Target>

      {isRegularMenuExitTransitioning ? null : (
        <Menu.Dropdown
          miw={220}
          style={{
            border: 'none',
          }}
          // When isContextOpenForThisRow is true, position the dropdown at the cursor
          // otherwise, it positions normally relative to the Menu.Target
          top={isContextOpenForThisRow ? info.y : undefined}
          left={isContextOpenForThisRow ? info.x : undefined}
        >
          {children}
        </Menu.Dropdown>
      )}
    </Menu>
  );
};

TableRowAction.Option = TableRowActionOption;
export { TableRowAction, TableRowActionOption };
export type { TableRowActionProps };
